import { INTEGRATION_PROVIDERS, IntegrationExport, SYNC_STATUSES } from '@vy/types';
import { fromNullToUndefined, isNotNullOrUndefined, raiseUnreachable } from '@vy/utils';
import type { InvoiceOverview } from '@vy/api-definition';
import { SyncStatusIcon } from '../SyncStatusIcon';
import { INTEGRATION_TYPES, SYNC_JOB_STATUSES } from '@vy/integrations-types';
import { useInvoiceSyncIntegrationsEnabled } from '../../../../../../hooks/requests/integrations';

type SyncStatusCellProps = {
  row: InvoiceOverview;
};

export const SyncStatusCell: React.FC<SyncStatusCellProps> = ({ row }) => {
  const { isNetSuiteEnabled, isStripeEnabled, isQuickBooksEnabled, isMorningEnabled, isXeroEnabled, isPaddleEnabled } =
    useInvoiceSyncIntegrationsEnabled();
  const stripeSyncStatus = row.stripeSyncStatus;
  const stripeSyncStatusUpdatedAt = row.stripeSyncStatusUpdatedAt;
  const invoiceId = row.id;
  const integrationExports = row.integrationExports;
  const quickBooksSyncData = getQuickBooksSyncData(integrationExports ?? []);
  const morningSyncData = getMorningSyncData(integrationExports ?? []);
  const netsuiteSyncData = getNetSuiteSyncData(integrationExports ?? []);
  const xeroSyncData = getXeroSyncData(integrationExports ?? []);
  const paddleSyncData = getPaddleSyncData(integrationExports ?? []);

  return (
    <div className="flex w-full items-center justify-center gap-1">
      {isNetSuiteEnabled && (
        <div>
          <SyncStatusIcon
            serviceName={INTEGRATION_PROVIDERS.netSuite}
            syncStatus={getSyncStatus(netsuiteSyncData)}
            syncStatusUpdatedAt={netsuiteSyncData?.date}
            invoiceId={invoiceId}
          />
        </div>
      )}

      {isStripeEnabled && (
        <div>
          <SyncStatusIcon
            serviceName={INTEGRATION_PROVIDERS.stripe}
            syncStatus={stripeSyncStatus}
            syncStatusUpdatedAt={fromNullToUndefined(stripeSyncStatusUpdatedAt)}
            invoiceId={row.stripeIntegrationExternalId}
          />
        </div>
      )}

      {isQuickBooksEnabled && (
        <div>
          <SyncStatusIcon
            serviceName={INTEGRATION_PROVIDERS.quickBooks}
            syncStatus={getSyncStatus(quickBooksSyncData)}
            syncStatusUpdatedAt={quickBooksSyncData?.date}
            invoiceId={quickBooksSyncData?.entityId}
          />
        </div>
      )}

      {isMorningEnabled && (
        <div>
          <SyncStatusIcon
            serviceName={INTEGRATION_PROVIDERS.morning}
            syncStatus={getSyncStatus(morningSyncData)}
            syncStatusUpdatedAt={morningSyncData?.date}
            invoiceId={morningSyncData?.entityId}
          />
        </div>
      )}

      {isXeroEnabled && (
        <div>
          <SyncStatusIcon
            serviceName={INTEGRATION_PROVIDERS.xero}
            syncStatus={getSyncStatus(xeroSyncData)}
            syncStatusUpdatedAt={xeroSyncData?.date}
            invoiceId={xeroSyncData?.entityId}
          />
        </div>
      )}

      {isPaddleEnabled && (
        <div>
          <SyncStatusIcon
            serviceName={INTEGRATION_PROVIDERS.paddle}
            syncStatus={getSyncStatus(paddleSyncData)}
            syncStatusUpdatedAt={paddleSyncData?.date}
            invoiceId={invoiceId}
          />
        </div>
      )}
    </div>
  );
};

function getQuickBooksSyncData(integrationExports: IntegrationExport[]) {
  return integrationExports.find(isQuickBooksIntegration);
}

function getMorningSyncData(integrationExports: IntegrationExport[]) {
  return integrationExports.find(isMorningIntegration);
}

function getNetSuiteSyncData(integrationExports: IntegrationExport[]) {
  return integrationExports.find(isNetSuiteIntegration);
}

function getXeroSyncData(integrationExports: IntegrationExport[]) {
  return integrationExports.find(isXeroIntegration);
}

function getSyncStatus(integrationExport: IntegrationExport | undefined) {
  return isNotNullOrUndefined(integrationExport)
    ? mapIntegrationStatusToSyncStatus(integrationExport)
    : SYNC_STATUSES.None;
}

function isQuickBooksIntegration(integrationExport: IntegrationExport) {
  return integrationExport.integration === INTEGRATION_TYPES.QUICKBOOKS;
}

function isMorningIntegration(integrationExport: IntegrationExport) {
  return integrationExport.integration === INTEGRATION_TYPES.MORNING;
}

function isNetSuiteIntegration(integrationExport: IntegrationExport) {
  return integrationExport.integration === INTEGRATION_TYPES.NETSUITE;
}

function isXeroIntegration(integrationExport: IntegrationExport) {
  return integrationExport.integration === INTEGRATION_TYPES.XERO;
}

function getPaddleSyncData(integrationExports: IntegrationExport[]) {
  return integrationExports.find(isPaddleIntegration);
}

function isPaddleIntegration(integrationExport: IntegrationExport) {
  return integrationExport.integration === INTEGRATION_TYPES.PADDLE;
}

function mapIntegrationStatusToSyncStatus(integrationExport: IntegrationExport) {
  switch (integrationExport.status) {
    case SYNC_JOB_STATUSES.COMPLETED:
      return SYNC_STATUSES.Synced;
    case SYNC_JOB_STATUSES.FAILED:
      return SYNC_STATUSES.Failed;
    case SYNC_JOB_STATUSES.PENDING:
    case SYNC_JOB_STATUSES.IN_PROGRESS:
      return SYNC_STATUSES.None;
    default:
      return raiseUnreachable(integrationExport.status);
  }
}
